import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type Props = {
  disabled?: boolean;
  /** When true, Block / Read / Delete stay disabled. */
  actionsDisabled?: boolean;
  isUnread?: boolean;
  deleteLabel?: string;
  onReply: () => void;
  onReplyAll: () => void;
  onForward: () => void;
  onNewEmail: () => void;
  onBlockSender: () => void;
  onToggleRead: () => void;
  onDelete: () => void;
  onCreateProject: () => void;
  onCreateTask: () => void;
  onNewAccount: () => void;
  onAddContact: () => void;
  onMerge?: () => void;
  addContactDisabled?: boolean;
  /** IT Management: hide CRM account/contact/project/task actions. */
  showCrmActions?: boolean;
};

const SCROLL_EDGE = 4;

function readScrollEdges(el: HTMLDivElement) {
  const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
  const left = el.scrollLeft;
  return {
    maxScroll,
    hasOverflow: maxScroll > SCROLL_EDGE,
    atStart: left <= SCROLL_EDGE,
    atEnd: left >= maxScroll - SCROLL_EDGE,
  };
}

export function TicketRibbon({
  disabled,
  actionsDisabled,
  isUnread,
  deleteLabel = 'Delete',
  onReply,
  onReplyAll,
  onForward,
  onNewEmail,
  onBlockSender,
  onToggleRead,
  onDelete,
  onCreateProject,
  onCreateTask,
  onNewAccount,
  onAddContact,
  onMerge,
  addContactDisabled,
  showCrmActions = true,
}: Props) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const replyRef = useRef<HTMLDivElement>(null);
  const caretRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [showPrevArrow, setShowPrevArrow] = useState(false);
  const [showNextArrow, setShowNextArrow] = useState(false);

  const updateScrollArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const { hasOverflow, atStart, atEnd } = readScrollEdges(el);
    setShowPrevArrow(hasOverflow && !atStart);
    setShowNextArrow(hasOverflow && !atEnd);
  }, []);

  const updateMenuPosition = useCallback(() => {
    const rect = caretRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      zIndex: 4000,
      minWidth: 140,
    });
  }, []);

  useLayoutEffect(() => {
    if (!replyOpen) return;
    updateMenuPosition();
  }, [replyOpen, updateMenuPosition]);

  useEffect(() => {
    if (!replyOpen) return undefined;
    function onDoc(ev: MouseEvent) {
      const target = ev.target as Node;
      if (replyRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setReplyOpen(false);
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') setReplyOpen(false);
    }
    function onReposition() {
      updateMenuPosition();
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [replyOpen, updateMenuPosition]);

  useEffect(() => {
    updateScrollArrows();
    const el = trackRef.current;
    if (!el) return undefined;
    const onScroll = () => updateScrollArrows();
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(updateScrollArrows);
    ro.observe(el);
    window.addEventListener('resize', updateScrollArrows);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
      window.removeEventListener('resize', updateScrollArrows);
    };
  }, [updateScrollArrows]);

  function scrollByPage(direction: -1 | 1) {
    const el = trackRef.current;
    if (!el) return;
    const step = Math.round(el.clientWidth * 0.75);
    const { maxScroll } = readScrollEdges(el);
    const target = Math.max(0, Math.min(maxScroll, el.scrollLeft + direction * step));
    el.scrollTo({ left: target, behavior: 'smooth' });
    window.setTimeout(updateScrollArrows, 350);
  }

  const replyMenu =
    replyOpen &&
    createPortal(
      <div ref={menuRef} className="tickets-ribbon__menu" role="menu" style={menuStyle}>
        <button
          type="button"
          role="menuitem"
          disabled={disabled}
          onClick={() => {
            setReplyOpen(false);
            onReply();
          }}
        >
          Reply
        </button>
        <button
          type="button"
          role="menuitem"
          disabled={disabled}
          onClick={() => {
            setReplyOpen(false);
            onForward();
          }}
        >
          Forward
        </button>
      </div>,
      document.body,
    );

  return (
    <div className="tickets-ribbon" role="toolbar" aria-label="Ticket actions">
      {showPrevArrow ? (
        <button
          type="button"
          className="tickets-ribbon__arrow tickets-ribbon__arrow--left"
          aria-label="Scroll actions left"
          onClick={() => scrollByPage(-1)}
        >
          <span aria-hidden>‹</span>
        </button>
      ) : null}

      <div className="tickets-ribbon__track" ref={trackRef}>
        <div className="tickets-ribbon__section tickets-ribbon__section--mail">
          <span className="tickets-ribbon__label">Mail</span>
          <div className="tickets-ribbon__group">
            <button type="button" className="tickets-ribbon__btn" onClick={onNewEmail}>
              <span className="tickets-ribbon__icon" aria-hidden>
                ✉
              </span>
              New email
            </button>
            <div className="tickets-ribbon__split" ref={replyRef}>
              <button
                type="button"
                className="tickets-ribbon__btn"
                disabled={disabled}
                onClick={onReplyAll}
              >
                <span className="tickets-ribbon__icon" aria-hidden>
                  ↩
                </span>
                Reply all
              </button>
              <button
                ref={caretRef}
                type="button"
                className="tickets-ribbon__btn tickets-ribbon__split-caret"
                disabled={disabled}
                aria-haspopup="menu"
                aria-expanded={replyOpen}
                aria-label="More reply options"
                onClick={() => setReplyOpen((v) => !v)}
              >
                ▾
              </button>
            </div>
          </div>
        </div>

        <div className="tickets-ribbon__section">
          <span className="tickets-ribbon__label">Actions</span>
          <div className="tickets-ribbon__group">
            <button
              type="button"
              className="tickets-ribbon__btn tickets-ribbon__btn--warn"
              disabled={actionsDisabled}
              onClick={onBlockSender}
            >
              Block sender
            </button>
            <button
              type="button"
              className="tickets-ribbon__btn"
              disabled={actionsDisabled}
              onClick={onToggleRead}
            >
              {isUnread ? 'Mark as read' : 'Mark as unread'}
            </button>
            <button
              type="button"
              className="tickets-ribbon__btn tickets-ribbon__btn--warn"
              disabled={actionsDisabled}
              onClick={onDelete}
            >
              {deleteLabel}
            </button>
            {onMerge ? (
              <button
                type="button"
                className="tickets-ribbon__btn"
                disabled={disabled}
                onClick={onMerge}
              >
                Merge…
              </button>
            ) : null}
          </div>
        </div>

        {showCrmActions ? (
          <div className="tickets-ribbon__section">
            <span className="tickets-ribbon__label">CRM</span>
            <div className="tickets-ribbon__group">
              <button type="button" className="tickets-ribbon__btn" onClick={onNewAccount}>
                New account
              </button>
              <button
                type="button"
                className="tickets-ribbon__btn"
                disabled={disabled || addContactDisabled}
                onClick={onAddContact}
              >
                Add contact
              </button>
              <button
                type="button"
                className="tickets-ribbon__btn tickets-ribbon__btn--accent"
                disabled={disabled}
                onClick={onCreateProject}
              >
                + Project
              </button>
              <button
                type="button"
                className="tickets-ribbon__btn tickets-ribbon__btn--accent"
                disabled={disabled}
                onClick={onCreateTask}
              >
                + Task
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {showNextArrow ? (
        <button
          type="button"
          className="tickets-ribbon__arrow tickets-ribbon__arrow--right"
          aria-label="Scroll actions right"
          onClick={() => scrollByPage(1)}
        >
          <span aria-hidden>›</span>
        </button>
      ) : null}

      {replyMenu}
    </div>
  );
}
